<template>
    <div
        class="overflow-hidden relative w-full"
        :class="[fullScreen ? 'h-full' : `h-0 ${padding}`]"
        :data-media-interactive="isSlider ? 'true' : null"
        @click="stopSliderClick">
        <img
            v-if="showPlaceholder"
            :src="placeholder"
            class="object-cover object-center absolute top-0 left-0 w-full h-full"
            alt="" />
        <!-- The video container is populated after the Kaltura JS player script loads. -->
        <div :id="videoContainerId" class="object-cover object-center absolute top-0 left-0 w-full h-full" />
        <PlayButton
            v-if="showPlaceholder"
            :aria-label="$globalLabels.play"
            @click="play" />

        <!-- Optional content slot -->
        <slot v-if="showPlaceholder && fullScreen" :play="play" />
    </div>
</template>

<script lang="ts">
import { createKalturaPlayer, destroyKalturaPlayer } from '../../kaltura-player';
import PlayButton from './PlayButton.vue';

export default {
    components: { PlayButton },
    inject: ['$globalLabels', '$lang'],
    props: {
        id: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        videoId: { type: String, required: true },
        playerId: { type: String, required: true },
        partnerId: { type: String, required: true },
        rendition: { type: String, default: 'landscape' },
        fullScreen: { type: Boolean, default: false },
        isSlider: { type: Boolean, default: false },
    },
    data() {
        return {
            videoContainerId: '',
            firstPlay: false,
            isInitializing: false,
            playerInstance: null,
        };
    },
    computed: {
        padding(): string {
            return this.rendition === 'landscape' ? 'pt-16/9' : 'pt-3/4';
        },
        showPlaceholder(): boolean {
            return !this.firstPlay && this.placeholder !== '';
        },
        textLanguage(): string {
            return (this.$lang || document.documentElement.lang || '').toString();
        },
    },
    /***********************
     * Vue lifecycle
     ***********************/
    mounted() {
        this.videoContainerId = this.id === '' ? 'video-player-' + Math.random().toString(16).slice(2) : this.id;
        if (!this.showPlaceholder) {
            this.initializePlayer();
        }
    },
    beforeUnmount() {
        this.destroyPlayer();
    },
    methods: {
        async initializePlayer(): Promise<void> {
            try {
                if (!this.videoContainerId || this.isInitializing) {
                    return;
                }
                this.isInitializing = true;

                this.destroyPlayer();
                this.playerInstance = await createKalturaPlayer({
                    targetId: this.videoContainerId,
                    partnerId: this.partnerId,
                    playerId: this.playerId,
                    entryId: this.videoId,
                    textLanguage: this.textLanguage,
                    disableTranscriptPlugin: true,
                });
            } catch (error) {
                console.error('Failed to initialize Kaltura player.', error);
            } finally {
                this.isInitializing = false;
            }
        },
        destroyPlayer(): void {
            destroyKalturaPlayer(this.playerInstance);
            this.playerInstance = null;
        },
        async play(): Promise<void> {
            if (!this.playerInstance) {
                await this.initializePlayer();
            }
            if (this.playerInstance && typeof this.playerInstance.play === 'function') {
                this.playerInstance.play();
                this.firstPlay = true;
            }
        },
        stopSliderClick(event: MouseEvent): void {
            if (this.isSlider) {
                event.stopPropagation();
            }
        },
    },
};
</script>
